import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import { useApprovalCounts } from "@/hooks/useApprovalCounts";
import { useOpenBillsCount } from "@/hooks/useBills";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Receipt, CreditCard, CalendarClock, DollarSign, Banknote } from "lucide-react";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const AccountingDashboardView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: metrics, isLoading } = useDashboardMetrics();
  const { data: approvals } = useApprovalCounts();
  const { data: openBillsCount } = useOpenBillsCount();

  const { data: collectedTotal } = useQuery({
    queryKey: ["collected_total"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("amount_paid")
        .is("deleted_at", null)
        .not("status", "eq", "void");
      if (error) throw error;
      return (data ?? []).reduce((s, r) => s + Number((r as any).amount_paid || 0), 0);
    },
    enabled: !!user,
  });

  const kpis = [
    { label: "Draft Invoices", value: approvals?.invoices ?? 0, icon: Receipt, fmt: String },
    { label: "Open Bills", value: openBillsCount ?? 0, icon: CreditCard, fmt: String },
    { label: "Collected", value: collectedTotal ?? 0, icon: Banknote, fmt },
    { label: "Actual Cost", value: metrics?.actualCost ?? 0, icon: DollarSign, fmt },
    { label: "Committed Cost", value: metrics?.committedCost ?? 0, icon: CalendarClock, fmt },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Accounting Dashboard</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {kpis.map((k) => (
          <Card key={k.label}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{k.label}</CardTitle>
              <k.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{isLoading ? "…" : k.fmt(k.value)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/controller/bills")}>
          <CardContent className="flex flex-col items-center py-8">
            <CreditCard className="h-8 w-8 text-muted-foreground mb-2" />
            <span className="font-medium">Bills</span>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/controller/invoices")}>
          <CardContent className="flex flex-col items-center py-8">
            <Receipt className="h-8 w-8 text-muted-foreground mb-2" />
            <span className="font-medium">Invoices</span>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/controller/periods")}>
          <CardContent className="flex flex-col items-center py-8">
            <CalendarClock className="h-8 w-8 text-muted-foreground mb-2" />
            <span className="font-medium">Periods</span>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
